import { useState, useEffect } from 'react';
import { getBookings, refundBooking, deleteBooking, getWorkers } from '../services/api';
import api from '../services/api';

const assignWorker = (bookingId, workerId) => 
  api.put(`/admin/bookings/${bookingId}/assign`, { worker_id: workerId }).then(r => r.data);

// Distance in km between two lat/lng points (haversine).
function distanceKm(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function Bookings({ userRole }) {
  const isSupport = userRole === 'support';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [workerId, setWorkerId] = useState('');
  const [platformFee, setPlatformFee] = useState(5);
  const [workers, setWorkers] = useState([]);        // all workers for the assign dropdown
  const [workerSearch, setWorkerSearch] = useState(''); // search box inside the dropdown

  useEffect(() => { loadBookings(); loadWorkers(); }, []);
  useEffect(() => {
    api.get('/settings/platform_convenience_fee')
      .then(r => { const v = parseFloat(r.data?.value); if (!isNaN(v)) setPlatformFee(v); })
      .catch(() => {});
  }, []);

  // const [assigningId, setAssigningId] = useState(null);
  // const [workerId, setWorkerId] = useState('');

  // useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    setLoading(true);
    try { setBookings(await getBookings()); } catch (e) {}
    setLoading(false);
  };

  const loadWorkers = async () => {
    try { setWorkers(await getWorkers()); } catch (e) {}
  };

  const handleAssign = async (bookingId, idOverride) => {
    const wid = idOverride ?? workerId;
    if (!wid) { alert('Select a worker'); return; }
    try { await assignWorker(bookingId, parseInt(wid)); alert('Worker assigned!'); setAssigningId(null); setWorkerId(''); setWorkerSearch(''); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleRefund = async (id) => {
    if (!window.confirm('Issue refund for this booking?')) return;
    try { await refundBooking(id); alert('Refund issued!'); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Refund failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Permanently delete Booking #${id}? This cannot be undone.`)) return;
    try { await deleteBooking(id); alert('Booking deleted!'); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking? Customer will be notified.')) return;
    try {
      await api.put(`/admin/bookings/${id}/cancel`);
      alert('Booking cancelled!');
      loadBookings();
    } catch (e) { alert(e.response?.data?.detail || 'Cancel failed'); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'badge-grey',
      confirmed: 'badge-info',
      in_progress: 'badge-warning',
      completed: 'badge-success',
      cancelled: 'badge-danger',
    };
    return <span className={`badge ${map[status] || 'badge-grey'}`}>{status}</span>;
  };

  const filtered = (filter === 'all' ? bookings : bookings.filter(b => b.status === filter))
    .filter(b => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return String(b.id).includes(q) ||
             (b.display_id || '').toLowerCase().includes(q) ||
             (b.customer?.name || '').toLowerCase().includes(q) ||
             (b.customer?.phone || '').includes(q) ||
             (b.worker?.user?.name || '').toLowerCase().includes(q) ||
             (b.service?.name || '').toLowerCase().includes(q);
    });

  return (
    <div>
      <div className="page-header">
        <h1>Bookings Management</h1>
        <p>View and manage all customer bookings</p>
      </div>
      <div className="card">
        <div className="card-header">
          <h2>📋 All Bookings ({filtered.length})</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="🔍 Search by ID, customer, worker, service..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: 280 }}
            />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="refresh-btn" onClick={loadBookings}>🔄 Refresh</button>
          </div>
        </div>
        {loading ? <div className="loading">Loading bookings...</div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Worker</th>
                  <th>Amount</th>
                  <th>Platform Fee</th>
                  <th>Worker Earning</th>
                  <th>Coupon</th>
                  <th>Status</th>
                  <th>Refund</th>
                  <th>Scheduled</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id}>
                    <td>{b.display_id || `#${b.id}`}</td>
                    <td>
                      {b.customer ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{b.customer.name}</div>
                          <div style={{ fontSize: 12, color: '#1565c0', fontWeight: 500 }}>📞 {b.customer.phone || '—'}</div>
                        </div>
                      ) : `User #${b.customer_id}`}
                    </td>
                    <td>
                      {b.worker ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{b.worker.user?.name || 'Worker'}</div>
                          <div style={{ fontSize: 12, color: '#00695c', fontWeight: 500 }}>📞 {b.worker.user?.phone || '—'}</div>
                          <div style={{ fontSize: 11, color: '#999' }}>{b.worker.skill_category}</div>
                        </div>
                      ) : <span className="badge badge-warning">Unassigned</span>}
                    </td>
                    <td>
                      {/* Gross = max(total_amount, paid online + wallet). Correct in every
                          case incl. pending re-revisions (prevents the 8+7=15 bug). */}
                      {(() => {
                        const paidOnline = Number(b.payment?.amount || 0);
                        const gross = Math.max(Number(b.total_amount || 0), paidOnline + Number(b.wallet_used || 0));
                        const online = Math.max(0, gross - Number(b.wallet_used || 0));
                        return (
                          <>
                            <strong>₹{gross.toFixed(2)}</strong>
                            {Number(b.wallet_used || 0) > 0 && (
                              <div style={{ fontSize: 10, color: '#7c3aed' }}>₹{Number(b.wallet_used).toFixed(2)} wallet + ₹{online.toFixed(2)} online</div>
                            )}
                            {Number(b.coupon_discount || 0) > 0 && (
                              <div style={{ fontSize: 10, color: '#999' }}>🎟️ ₹{Number(b.coupon_discount).toFixed(2)} coupon</div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {b.platform_fee > 0 ? (
                        <div>
                          <strong>₹{Number(b.platform_fee || 0).toFixed(2)}</strong>
                          <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                            ₹{platformFee.toFixed(2)} fee + ₹{Math.max(0, (b.platform_fee - platformFee)).toFixed(2)} comm

                            {/* ₹5 fee + ₹{Math.max(0, (b.platform_fee - 5)).toFixed(2)} comm */}
                          </div>
                        </div>
                      ) : <span style={{color:'#ccc'}}>₹0.00</span>}
                    </td>
                    <td>₹{Number(b.worker_earning || 0).toFixed(2)}</td>
                    <td>
                      {b.coupon_code ? (
                        <div>
                          <span className="badge badge-info" style={{fontSize:10}}>{b.coupon_code}</span>
                          <div style={{fontSize:10, color:'#7c3aed'}}>-₹{Number(b.coupon_discount || 0).toFixed(0)}</div>
                        </div>
                      ) : <span style={{color:'#ccc'}}>—</span>}
                    </td>
                    <td>{statusBadge(b.status)}</td>
                    <td>
                      {b.payment?.status === 'refunded' ? (
                        <div>
                          <span className="badge badge-success">✅ Bank Refund Processed</span>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>
                            ₹{Math.max(0, Number(b.payment?.amount || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0)).toFixed(2)} to bank

                            {/* ₹{Number(b.payment?.amount || b.total_amount || 0).toFixed(2)} to bank */}
                          </div>
                          <div style={{ fontSize: 10, color: '#999' }}>credited in 2–7 business days</div>

                          {/* Wallet portion (net of any visiting charge) was also credited
                              back — keep showing it after the bank refund completes. */}
                          {Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0))) > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>+ ₹{Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0))).toFixed(2)} to wallet</div>
                          )}
                          {Number(b.coupon_discount || 0) > 0 && (
                            <div style={{ fontSize: 10, color: '#7c3aed' }}>🎟️ Coupon ₹{Number(b.coupon_discount).toFixed(2)} not refunded (reusable)</div>
                          )}
                        </div>

                      ) : b.payment?.status === 'wallet_refunded' ? (
                        // Wallet-choice refund: bank portion (net of visiting charges) +
                        // wallet portion (net of visiting charges) were BOTH credited to wallet.
                        (() => {
                          const bankNet   = Math.max(0, Number(b.payment?.amount || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0));
                          const walletNet = Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0)));
                          const total = (bankNet + walletNet).toFixed(2);
                          return (
                            <div>
                              <span className="badge badge-info">💰 Wallet Refunded</span>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginTop: 2 }}>₹{total} to wallet</div>
                              {(bankNet > 0 && walletNet > 0) && (
                                <div style={{ fontSize: 10, color: '#999' }}>₹{walletNet.toFixed(2)} wallet + ₹{bankNet.toFixed(2)} bank</div>
                              )}
                              {Number(b.coupon_discount || 0) > 0 && (
                                <div style={{ fontSize: 10, color: '#7c3aed' }}>🎟️ Coupon ₹{Number(b.coupon_discount).toFixed(2)} not refunded (reusable)</div>
                              )}
                            </div>
                          );
                        })()
                      ) : b.payment?.status === 'refund_initiated' ? (
                        // Bank-choice refund: bank amount goes to bank; any wallet used
                        // (net of visiting charges) was credited back to the wallet.
                        (() => {
                          // const walletNet = Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0)));
                          const bankRefund = Math.max(0, Number(b.payment?.amount || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0));
                          const walletNet = Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0)));

                          return (
                            <div>
                              <span className="badge badge-warning">⏳ Refund Initiated</span>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706', marginTop: 2 }}>
                                ₹{bankRefund.toFixed(2)} to bank

                                {/* ₹{Number(b.payment?.amount || b.total_amount || 0).toFixed(2)} to bank */}
                              </div>
                              {walletNet > 0 && (
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>+ ₹{walletNet.toFixed(2)} to wallet</div>
                              )}
                              <div style={{ fontSize: 10, color: '#999' }}>2–7 business days</div>
                              {Number(b.coupon_discount || 0) > 0 && (
                                <div style={{ fontSize: 10, color: '#7c3aed' }}>🎟️ Coupon ₹{Number(b.coupon_discount).toFixed(2)} not refunded (reusable)</div>
                              )}
                            </div>
                          );
                        })()
                      ) : b.status === 'cancelled' && b.payment?.status === 'paid' ? (
                        <div>
                          <span className="badge badge-danger">⚠️ Refund Pending</span>
                          {/* Net wallet refund = wallet used MINUS the portion consumed by
                              visiting charges (worker_earning + platform_fee beyond the bank amount).
                              Prevents showing "wallet credited" when the worker took the full amount. */}
                          <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>₹{Math.max(0, Number(b.payment?.amount || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0)).toFixed(2)} bank pending{Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0))) > 0 ? ` • ₹${Math.max(0, Number(b.wallet_used || 0) - Math.max(0, Number(b.worker_earning || 0) + Number(b.platform_fee || 0) - Number(b.payment?.amount || 0))).toFixed(2)} wallet credited` : ''}</div>
                        </div>
                      ) : b.status === 'cancelled' && !b.payment && (b.wallet_used || b.discount_applied) > 0 ? (
                        Math.max(0, Number(b.wallet_used || b.discount_applied || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0)) > 0 ? (
                          <div>
                            <span className="badge badge-info">💰 Wallet Refunded</span>
                            <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 2 }}>₹{Math.max(0, Number(b.wallet_used || b.discount_applied || 0) - Number(b.worker_earning || 0) - Number(b.platform_fee || 0)).toFixed(2)} to wallet</div>
                            {Number(b.coupon_discount || 0) > 0 && (
                              <div style={{ fontSize: 10, color: '#7c3aed' }}>🎟️ Coupon ₹{Number(b.coupon_discount).toFixed(2)} not refunded (reusable)</div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="badge badge-warning">🧰 Visiting Charges</span>
                            <div style={{ fontSize: 12, color: '#d97706', marginTop: 2 }}>₹{Number(b.worker_earning || 0).toFixed(2)} to partner • No refund</div>
                            {Number(b.coupon_discount || 0) > 0 && (
                              <div style={{ fontSize: 10, color: '#7c3aed' }}>🎟️ Coupon ₹{Number(b.coupon_discount).toFixed(2)} not refunded (reusable)</div>
                            )}
                          </div>
                        )
                      ) : b.payment?.status === 'paid' ? (
                        <span className="badge badge-grey">Not Refunded</span>
                      ) : (
                        <span style={{ color: '#ccc' }}>—</span>
                      )}
                    </td>
                    <td>{b.scheduled_at?.substring(0, 16)}</td>
                    <td>
                      {assigningId === b.id ? (
                        <div style={{width:280}}>
                          <input autoFocus placeholder="🔍 Search worker by name / skill"
                            value={workerSearch} onChange={e=>setWorkerSearch(e.target.value)}
                            style={{width:'100%', padding:'6px 8px', border:'1px solid #ddd', borderRadius:6, marginBottom:6, boxSizing:'border-box'}} />
                          <div style={{maxHeight:220, overflowY:'auto', border:'1px solid #eee', borderRadius:6}}>
                            {(() => {
                              const q = workerSearch.trim().toLowerCase();
                              // Match the booking's service category, compute distance, sort nearest-first.
                              const list = workers
                                .map(w => ({
                                  w,
                                  dist: distanceKm(b.latitude, b.longitude, w.latitude, w.longitude),
                                }))
                                .filter(({ w }) => {
                                  if (!q) return true;
                                  const name = (w.user?.name || '').toLowerCase();
                                  const skill = ((w.skills || w.skill_category) || '').toLowerCase();
                                  return name.includes(q) || skill.includes(q) || String(w.id).includes(q);
                                })
                                .sort((a, c) => {
                                  if (a.dist == null) return 1;
                                  if (c.dist == null) return -1;
                                  return a.dist - c.dist;
                                });
                              if (list.length === 0) return <div style={{padding:8, fontSize:12, color:'#999'}}>No workers found</div>;
                              return list.slice(0, 50).map(({ w, dist }) => {
                                const matchesSkill = ((w.skills || w.skill_category) || '').toLowerCase().includes((b.service?.category || '').toLowerCase());
                                return (
                                  <div key={w.id} onClick={() => handleAssign(b.id, w.id)}
                                    style={{padding:'6px 8px', borderBottom:'1px solid #f2f2f2', cursor:'pointer', fontSize:12,
                                            background: w.id === b.worker_id ? '#eef' : '#fff'}}>
                                    <div style={{fontWeight:600}}>
                                      {w.user?.name || `Worker #${w.id}`} <span style={{color:'#999', fontWeight:400}}>#{w.id}</span>
                                    </div>
                                    <div style={{color:'#666'}}>
                                      {w.skills || w.skill_category}
                                      {matchesSkill ? ' ✅' : ''}
                                      {dist != null ? `  •  ${dist.toFixed(1)} km away` : '  •  no location'}
                                      {w.is_available === false ? '  •  🔴 offline' : '  •  🟢 online'}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          <button className="btn btn-sm btn-danger" style={{marginTop:6}} onClick={()=>{setAssigningId(null); setWorkerSearch('');}}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                          {!['completed','cancelled'].includes(b.status) && (
                            <button className="btn btn-sm btn-warning" onClick={()=>{setAssigningId(b.id); setWorkerId(b.worker_id?.toString() || '');}}>🔧 {b.worker_id ? 'Reassign' : 'Assign'}</button>
                          )}
                          {!['completed','cancelled'].includes(b.status) && (
                            <button className="btn btn-sm" style={{background:'#e53e3e',color:'#fff'}} onClick={() => handleCancel(b.id)}>❌ Cancel</button>
                          )}
                          {!isSupport && !['refunded','refund_initiated','wallet_refunded'].includes(b.payment?.status) && !['cancelled','completed'].includes(b.status) && (
                            <button className="btn btn-sm btn-danger" onClick={() => handleRefund(b.id)}>💸 Refund</button>
                          )}
                          {!isSupport && b.status === 'cancelled' && b.payment?.status === 'paid' && (
                            <button className="btn btn-sm" style={{background:'#f59e0b',color:'#fff'}} onClick={() => handleRefund(b.id)}>🔄 Retry Refund</button>
                          )}
                          {!isSupport && <button className="btn btn-sm" style={{background:'#666',color:'#fff'}} onClick={() => handleDelete(b.id)}>🗑️ Delete</button>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
